import React, { useState } from 'react';
import { PlusIcon, XIcon, EditIcon, TrashIcon, ClockIcon, FilterIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import { NotificationRule, NotificationEvent, NotificationChannel, NotificationScope } from './NotificationsManagement';
import { StatusBadge } from '../../StatusBadge';
import { OrgUnitSelector } from '../orgunit/OrgUnitSelector';
import type { OrgUnit } from '../../financial/sharedFinancialData';
interface NotificationRulesProps {
  globalRules: NotificationRule[];
  orgUnitRules: NotificationRule[];
  setRules: React.Dispatch<React.SetStateAction<NotificationRule[]>>;
  globalEvents: NotificationEvent[];
  orgUnitEvents: NotificationEvent[];
  setEvents: React.Dispatch<React.SetStateAction<NotificationEvent[]>>;
  channels: NotificationChannel[];
  locale: string;
}
export function NotificationRules({
  globalRules,
  orgUnitRules,
  setRules,
  globalEvents,
  orgUnitEvents,
  setEvents,
  channels,
  locale
}: NotificationRulesProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [frequency, setFrequency] = useState<'immediate' | 'daily' | 'weekly'>('immediate');
  const [ruleScope, setRuleScope] = useState<NotificationScope>('Global');
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnit | null>(null);
  const allRules = [...globalRules, ...orgUnitRules];
  const allEvents = [...globalEvents, ...orgUnitEvents];
  const getEventById = (eventId: string) => {
    return allEvents.find(event => event.id === eventId);
  };
  const getChannelById = (channelId: string) => {
    return channels.find(channel => channel.id === channelId);
  };
  const getFrequencyLabel = (freq: string) => {
    switch (freq) {
      case 'immediate':
        return 'Immediate';
      case 'daily':
        return 'Daily Digest';
      case 'weekly':
        return 'Weekly Digest';
      default:
        return freq;
    }
  };
  const handleToggleRule = (ruleId: string) => {
    setRules(rules => rules.map(rule => rule.id === ruleId ? {
      ...rule,
      isEnabled: !rule.isEnabled
    } : rule));
  };
  const handleEditRule = (rule: NotificationRule) => {
    setEditingRule(rule);
    setSelectedEventIds([rule.eventId]);
    setSelectedChannelIds(rule.channels);
    setFrequency(rule.frequency);
    setRuleScope(rule.scope);
    setIsDrawerOpen(true);
  };
  const handleDeleteRule = (ruleId: string) => {
    setRules(rules => rules.filter(rule => rule.id !== ruleId));
  };
  const handleAddRule = () => {
    setEditingRule(null);
    setSelectedEventIds([]);
    setSelectedChannelIds([]);
    setFrequency('immediate');
    setRuleScope('Global');
    setSelectedOrgUnit(null);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setEditingRule(null);
    setSelectedOrgUnit(null);
  };
  const handleOrgUnitChange = (orgUnitId: string, orgUnit: OrgUnit | null) => {
    setSelectedOrgUnit(orgUnit);
  };
  const handleSaveRule = () => {
    if (selectedEventIds.length === 0 || selectedChannelIds.length === 0) {
      return;
    }
    if (ruleScope === 'OrgUnit' && !selectedOrgUnit) {
      return;
    }
    const newRules = selectedEventIds.map(eventId => {
      const event = getEventById(eventId);
      if (!event) return null;
      const ruleData: NotificationRule = {
        id: editingRule?.id || `rule-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        eventId,
        name: `${event.name} Notifications`,
        description: `Send notifications for ${event.name.toLowerCase()}`,
        channels: selectedChannelIds,
        frequency,
        isEnabled: true,
        filters: {},
        scope: ruleScope,
        ...(ruleScope === 'OrgUnit' && selectedOrgUnit ? {
          orgUnitId: selectedOrgUnit.id,
          orgUnitName: selectedOrgUnit.name,
          orgUnitPath: selectedOrgUnit.path
        } : {}),
        createdAt: editingRule?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      return ruleData;
    }).filter(Boolean) as NotificationRule[];
    if (editingRule) {
      setRules(rules => rules.map(rule => rule.id === editingRule.id ? newRules[0] : rule));
    } else {
      setRules(rules => [...rules, ...newRules]);
    }
    setIsDrawerOpen(false);
  };
  const renderRulesList = (rules: NotificationRule[], showOrgUnit: boolean = false) => {
    return <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-19345">
        <ul className="divide-y divide-gray-200" data-id="element-19346">
          {rules.length === 0 ? <li className="px-6 py-12 text-center" data-id="element-19347">
              <p className="text-sm text-gray-500" data-id="element-19348">
                No notification rules configured.
              </p>
            </li> : rules.map(rule => {
          const event = getEventById(rule.eventId);
          return <li key={rule.id} data-id="element-19349">
                  <div className="px-4 py-4 sm:px-6" data-id="element-19350">
                    {showOrgUnit && rule.orgUnitName && <div className="mb-2" data-id="element-19351">
                        <p className="text-xs font-medium text-gray-500" data-id="element-19352">
                          {rule.orgUnitName}
                        </p>
                        <p className="text-xs text-gray-400" data-id="element-19353">
                          {rule.orgUnitPath}
                        </p>
                      </div>}
                    <div className="flex items-center justify-between" data-id="element-19354">
                      <div className="flex items-center" data-id="element-19355">
                        <button onClick={() => handleToggleRule(rule.id)} className={`mr-3 h-5 w-5 rounded-full flex items-center justify-center ${rule.isEnabled ? 'text-green-500 bg-green-50' : 'text-gray-400 bg-gray-100'}`} data-id="element-19356">
                          {rule.isEnabled ? <CheckCircleIcon className="h-5 w-5" data-id="element-19357" /> : <XCircleIcon className="h-5 w-5" data-id="element-19358" />}
                        </button>
                        <p className="text-sm font-medium text-indigo-600 truncate" data-id="element-19359">
                          {rule.name}
                        </p>
                      </div>
                      <div className="ml-2 flex-shrink-0 flex" data-id="element-19360">
                        <StatusBadge status={rule.isEnabled ? 'success' : 'error'} text={rule.isEnabled ? 'Enabled' : 'Disabled'} data-id="element-19361" />
                      </div>
                    </div>
                    <div className="mt-2 sm:flex sm:justify-between" data-id="element-19362">
                      <div className="sm:flex" data-id="element-19363">
                        <p className="flex items-center text-sm text-gray-500" data-id="element-19364">
                          Event: {event?.name || 'Unknown Event'}
                        </p>
                        <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6" data-id="element-19365">
                          <ClockIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" aria-hidden="true" data-id="element-19366" />
                          {getFrequencyLabel(rule.frequency)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0" data-id="element-19367">
                        <p data-id="element-19368">
                          Channels:{' '}
                          {rule.channels.map(channelId => getChannelById(channelId)?.name || 'Unknown').join(', ')}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-end space-x-2" data-id="element-19369">
                      <button type="button" onClick={() => handleEditRule(rule)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19370">
                        <EditIcon className="h-4 w-4" aria-hidden="true" data-id="element-19371" />
                      </button>
                      <button type="button" onClick={() => handleDeleteRule(rule.id)} className="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-19372">
                        <TrashIcon className="h-4 w-4" aria-hidden="true" data-id="element-19373" />
                      </button>
                    </div>
                  </div>
                </li>;
        })}
        </ul>
      </div>;
  };
  return <div className="space-y-8" data-id="element-19374">
      {/* Global Rules */}
      <div data-id="element-19375">
        <div className="mb-6 flex justify-between items-center" data-id="element-19376">
          <div data-id="element-19377">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19378">
              Global Notification Rules
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-19379">
              Global rules and events available to all organisation units
            </p>
          </div>
          <button type="button" onClick={handleAddRule} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19380">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-19381" />
            Add Rule
          </button>
        </div>
        {renderRulesList(globalRules, false)}
      </div>

      {/* OrgUnit Rules */}
      <div data-id="element-19382">
        <div className="mb-6" data-id="element-19383">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-19384">
            Organisation Unit Rules
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-19385">
            Rules and events specific to individual organisation units
          </p>
        </div>
        {renderRulesList(orgUnitRules, true)}
      </div>

      {/* Rule Configuration Drawer */}
      {isDrawerOpen && <div className="fixed inset-0 overflow-hidden z-50" data-id="element-19386">
          <div className="absolute inset-0 overflow-hidden" data-id="element-19387">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDrawer} data-id="element-19388"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-19389">
              <div className="relative w-screen max-w-md" data-id="element-19390">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-19391">
                  <div className="px-4 py-6 sm:px-6" data-id="element-19392">
                    <div className="flex items-start justify-between" data-id="element-19393">
                      <h2 className="text-lg font-medium text-gray-900" data-id="element-19394">
                        {editingRule ? 'Edit Rule' : 'Create New Rule'}
                      </h2>
                      <div className="ml-3 h-7 flex items-center" data-id="element-19395">
                        <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19396">
                          <span className="sr-only" data-id="element-19397">Close panel</span>
                          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-19398" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 py-6 overflow-y-auto px-4 sm:px-6" data-id="element-19399">
                    <div className="space-y-6" data-id="element-19400">
                      {/* Scope Selector */}
                      <div data-id="element-19401">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19402">
                          Scope <span className="text-red-500" data-id="element-19403">*</span>
                        </label>
                        <select value={ruleScope} onChange={e => setRuleScope(e.target.value as NotificationScope)} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19404">
                          <option value="Global" data-id="element-19405">Global</option>
                          <option value="OrgUnit" data-id="element-19406">Organisation Unit</option>
                        </select>
                        <p className="mt-1 text-xs text-gray-500" data-id="element-19407">
                          {ruleScope === 'Global' ? 'Available to all organisation units' : 'Specific to selected organisation unit'}
                        </p>
                      </div>

                      {/* OrgUnit Selector */}
                      {ruleScope === 'OrgUnit' && <div data-id="element-19408">
                          <OrgUnitSelector value={selectedOrgUnit?.id || ''} onChange={handleOrgUnitChange} label="Organisation Unit" placeholder="Select organisation unit" required={true} showPath={true} data-id="element-19409" />
                        </div>}

                      {/* Event Selection */}
                      <div data-id="element-19410">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19411">
                          Select Events
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19412">
                          {allEvents.filter(event => event.scope === ruleScope || ruleScope === 'OrgUnit' && event.scope === 'OrgUnit' && event.orgUnitId === selectedOrgUnit?.id).map(event => <div key={event.id} className="relative flex items-start" data-id="element-19413">
                                <div className="flex items-center h-5" data-id="element-19414">
                                  <input id={`event-${event.id}`} type="checkbox" checked={selectedEventIds.includes(event.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedEventIds([...selectedEventIds, event.id]);
                            } else {
                              setSelectedEventIds(selectedEventIds.filter(id => id !== event.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19415" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-19416">
                                  <label htmlFor={`event-${event.id}`} className="font-medium text-gray-700" data-id="element-19417">
                                    {event.name}
                                  </label>
                                  <p className="text-gray-500" data-id="element-19418">
                                    {event.description}
                                  </p>
                                </div>
                              </div>)}
                        </div>
                      </div>

                      {/* Channel Selection */}
                      <div data-id="element-19419">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19420">
                          Delivery Channels
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19421">
                          {channels.filter(channel => channel.isConfigured && (channel.scope === 'Global' || ruleScope === 'OrgUnit' && channel.scope === 'OrgUnit' && channel.orgUnitId === selectedOrgUnit?.id)).map(channel => <div key={channel.id} className="relative flex items-start" data-id="element-19422">
                                <div className="flex items-center h-5" data-id="element-19423">
                                  <input id={`channel-${channel.id}`} type="checkbox" checked={selectedChannelIds.includes(channel.id)} onChange={e => {
                            if (e.target.checked) {
                              setSelectedChannelIds([...selectedChannelIds, channel.id]);
                            } else {
                              setSelectedChannelIds(selectedChannelIds.filter(id => id !== channel.id));
                            }
                          }} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-19424" />
                                </div>
                                <div className="ml-3 text-sm" data-id="element-19425">
                                  <label htmlFor={`channel-${channel.id}`} className="font-medium text-gray-700" data-id="element-19426">
                                    {channel.name}
                                  </label>
                                </div>
                              </div>)}
                        </div>
                      </div>

                      {/* Frequency Selection */}
                      <div data-id="element-19427">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-19428">
                          Notification Frequency
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-19429">
                          <div className="relative flex items-start" data-id="element-19430">
                            <div className="flex items-center h-5" data-id="element-19431">
                              <input id="frequency-immediate" type="radio" checked={frequency === 'immediate'} onChange={() => setFrequency('immediate')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19432" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19433">
                              <label htmlFor="frequency-immediate" className="font-medium text-gray-700" data-id="element-19434">
                                Immediate
                              </label>
                              <p className="text-gray-500" data-id="element-19435">
                                Send notifications as events occur
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-19436">
                            <div className="flex items-center h-5" data-id="element-19437">
                              <input id="frequency-daily" type="radio" checked={frequency === 'daily'} onChange={() => setFrequency('daily')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19438" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19439">
                              <label htmlFor="frequency-daily" className="font-medium text-gray-700" data-id="element-19440">
                                Daily Digest
                              </label>
                              <p className="text-gray-500" data-id="element-19441">
                                Group notifications and send once per day
                              </p>
                            </div>
                          </div>
                          <div className="relative flex items-start" data-id="element-19442">
                            <div className="flex items-center h-5" data-id="element-19443">
                              <input id="frequency-weekly" type="radio" checked={frequency === 'weekly'} onChange={() => setFrequency('weekly')} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300" data-id="element-19444" />
                            </div>
                            <div className="ml-3 text-sm" data-id="element-19445">
                              <label htmlFor="frequency-weekly" className="font-medium text-gray-700" data-id="element-19446">
                                Weekly Digest
                              </label>
                              <p className="text-gray-500" data-id="element-19447">
                                Group notifications and send once per week
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Optional Filters */}
                      <div data-id="element-19448">
                        <div className="flex items-center justify-between" data-id="element-19449">
                          <label className="block text-sm font-medium text-gray-700" data-id="element-19450">
                            Additional Filters (Optional)
                          </label>
                          <button type="button" className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19451">
                            <FilterIcon className="h-4 w-4 mr-1" data-id="element-19452" />
                            Add Filter
                          </button>
                        </div>
                        <div className="mt-2" data-id="element-19453">
                          <p className="text-sm text-gray-500" data-id="element-19454">
                            No filters applied. Notifications will be sent for
                            all matching events.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 px-4 py-4 flex justify-end border-t border-gray-200" data-id="element-19455">
                    <button type="button" className="mr-3 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleCloseDrawer} data-id="element-19456">
                      Cancel
                    </button>
                    <button type="button" className="bg-indigo-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" onClick={handleSaveRule} data-id="element-19457">
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}